/**
 * Caché en memoria (a nivel de módulo) para catálogos que cambian poco
 * (menús, platos, ingredientes, materiales, alérgenos, plantillas de menaje).
 *
 * Motivo: páginas como el detalle de presupuesto hacían `getAll()` de TODOS
 * los catálogos en cada apertura, sin paginar. Eso es lento y consume memoria
 * (candidato a los OOM de producción). Con esta caché, al navegar entre
 * presupuestos dentro de una ventana corta no se vuelven a pedir.
 *
 * Diseño defensivo: si el fetcher falla, NO se cachea y se propaga el error
 * (el llamante decide). Un acierto de caché devuelve la MISMA respuesta; no
 * mutes su `.data` in situ (los consumidores actuales solo leen).
 */

const TTL_MS = 5 * 60 * 1000; // 5 minutos
const _cache = {}; // { key: { ts: number, data: any } }

/**
 * Devuelve el valor cacheado si es fresco (< TTL); si no, ejecuta `fetcher`,
 * guarda el resultado y lo devuelve.
 * @param {string} key       Clave lógica del catálogo (ej. 'menus').
 * @param {() => Promise<any>} fetcher  Llamada real a la API.
 */
export async function getCached(key, fetcher) {
  const now = Date.now();
  const hit = _cache[key];
  if (hit && (now - hit.ts) < TTL_MS) {
    return hit.data;
  }
  const data = await fetcher(); // si falla, no cacheamos y propaga
  _cache[key] = { ts: now, data };
  return data;
}

/** Invalida una clave concreta o, sin argumento, toda la caché. */
export function invalidateCatalogCache(key) {
  if (key) {
    delete _cache[key];
  } else {
    Object.keys(_cache).forEach((k) => delete _cache[k]);
  }
}
